// EscapeMap frontend entry point (docs/DESIGN.md §6). Loads registry + index + geometry once,
// wires the controls, and re-renders the choropleth (feature-state only) on every state change.
import "maplibre-gl/dist/maplibre-gl.css";
import maplibregl from "maplibre-gl";
import { Protocol } from "pmtiles";

import {
  dataFileExists,
  loadMetric,
  loadOutline,
  loadPalettes,
  loadPlzGeoJson,
  loadPlzIndex,
  loadRegistry,
  type MetricFile,
  type PlzIndex,
  type Registry,
  type RegistryMetric,
} from "./registry";
import {
  absDomain,
  decodeRow,
  diffDomain,
  difference,
  formatBandLabel,
  valueFor,
  type AppState,
  type MonthSelection,
  type ReachValue,
} from "./logic";
import { stopsFor, fillColorExpression } from "./scales";
import { AppStateStore } from "./state";
import { EscapeMapView, type PlzSourceConfig } from "./map/map";
import { renderSegmented, type SegmentedItem } from "./ui/controls";
import { createReachSlider, type ReachSlider } from "./ui/reachslider";
import { initSearchBox } from "./ui/searchbox";
import { renderLegend } from "./ui/legend";
import { renderDetails } from "./ui/details";
import { tooltipHtml } from "./ui/tooltip";
import { loadPairwiseMatrix, PairwiseUnavailableError, type PairwiseMatrix } from "./pairwise";

maplibregl.addProtocol("pmtiles", new Protocol().tile);

const MONTH_LABELS_FALLBACK = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function el<T extends HTMLElement>(id: string): T {
  const found = document.getElementById(id);
  if (!found) throw new Error(`missing #${id} in index.html`);
  return found as T;
}

/** Dev-only extensibility hook: `#...&dev=scalar` injects a scalar (no-month-dim) test metric,
 * computed client-side from elev_ref, WITHOUT touching web/public/data/registry.json. */
function maybeInjectScalarMetric(registry: Registry, plzIndex: PlzIndex, metricCache: Map<string, MetricFile>): void {
  if (!location.hash.includes("dev=scalar")) return;
  const id = "test_scalar";
  const metric: RegistryMetric = {
    id,
    label: "Elevation (dev test scalar)",
    short: "Elev/1000",
    unit: "km",
    decimals: 2,
    dims: {}, // no month dim -> month control must hide for this metric
    diff: { scale: "diverging", palette: "temp", domain: null },
    abs: { scale: "sequential", palette: "temp_abs", domain: null },
    file: "", // never fetched: values are supplied directly below
    source: "dev hook: elev_ref / 1000",
    method_url: null,
  };
  registry.metrics.push(metric);
  const values: Record<string, (number | null)[]> = {};
  for (const [plz, record] of Object.entries(plzIndex)) {
    values[plz] = [record.elev_ref / 1000];
  }
  metricCache.set(id, {
    metric: id,
    dims: [],
    shape: [],
    generated: new Date().toISOString(),
    source: "dev-hook",
    source_version: "0",
    values,
    meta: {},
  });
}

async function main(): Promise<void> {
  const loading = el<HTMLDivElement>("loading");
  const errorBanner = el<HTMLDivElement>("error-banner");

  const [registry, plzIndex, palettes, outline] = await Promise.all([
    loadRegistry(),
    loadPlzIndex(),
    loadPalettes(),
    loadOutline(),
  ]);

  const metricCache = new Map<string, MetricFile>();
  maybeInjectScalarMetric(registry, plzIndex, metricCache);

  // --- pairwise (reference x PLZ) matrix: loaded once at startup, independent of which metric
  // is selected, because the "Reachable within" filter applies to every metric (WP10 brief §2).
  interface PairwiseRuntime {
    metric: RegistryMetric;
    matrix: PairwiseMatrix;
  }
  let pairwiseRuntime: PairwiseRuntime | null = null;
  let reachSlider: ReachSlider | null = null;
  const TRAVEL_TIME_ATTRIBUTION = "Travel times © ARE, national passenger transport model NPVM 2023 (CC BY 4.0)";
  // Set only for a genuine data-integrity problem (meta/plz_index mismatch) — kept distinct from
  // a plain 404 (feature just not published yet), which is silent per the WP10 brief.
  let pairwiseError: string | null = null;
  const pairwiseMetricDef = registry.metrics.find((m) => m.kind === "pairwise");
  if (pairwiseMetricDef) {
    try {
      const matrix = await loadPairwiseMatrix(pairwiseMetricDef);
      if (matrix) {
        const plzKeys = Object.keys(plzIndex);
        const sameSize = matrix.meta.n === plzKeys.length;
        const sameKeys = sameSize && matrix.meta.plz_order.every((p) => p in plzIndex);
        if (sameSize && sameKeys) {
          pairwiseRuntime = { metric: pairwiseMetricDef, matrix };
        } else {
          pairwiseError = `Travel-time data (${pairwiseMetricDef.id}) does not match the PLZ index (${matrix.meta.n} vs ${plzKeys.length} postal codes) and has been disabled.`;
          console.warn(
            `pairwise metric "${pairwiseMetricDef.id}": meta.plz_order (n=${matrix.meta.n}) does not match plz_index (n=${plzKeys.length}); disabling the reach filter and travel-time metric`,
          );
        }
      }
    } catch (err) {
      if (err instanceof PairwiseUnavailableError) {
        // Data not published yet (404 on meta_file): silently disable, per the WP10 brief —
        // the rest of the app is unaffected.
        console.warn(`pairwise metric "${pairwiseMetricDef.id}" unavailable: ${err.message}`);
      } else {
        console.warn(`pairwise metric "${pairwiseMetricDef.id}" failed to load: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }

  async function loadMetricCached(metric: RegistryMetric): Promise<MetricFile> {
    const cached = metricCache.get(metric.id);
    if (cached) return cached;
    const file = await loadMetric(metric);
    metricCache.set(metric.id, file);
    return file;
  }

  // Prefer PMTiles when the pipeline published them; fall back to plain GeoJSON otherwise.
  const hasPmtiles = await dataFileExists("plz.pmtiles");
  const plzSource: PlzSourceConfig = hasPmtiles
    ? { kind: "vector", url: "pmtiles://" + new URL("./data/plz.pmtiles", location.href).href, sourceLayer: "plz" }
    : { kind: "geojson", data: await loadPlzGeoJson() };

  const store = new AppStateStore(registry, plzIndex);

  const metricPickerEl = el<HTMLDivElement>("metric-picker");
  const monthPickerEl = el<HTMLDivElement>("month-picker");
  const viewToggleEl = el<HTMLDivElement>("view-toggle");
  const reachGroupEl = el<HTMLDivElement>("reach-group");
  const reachPickerEl = el<HTMLDivElement>("reach-picker");
  const reachValueEl = el<HTMLOutputElement>("reach-value");
  const legendEl = el<HTMLDivElement>("legend");
  const detailsEl = el<HTMLDivElement>("details");
  const detailsBodyEl = el<HTMLDivElement>("details-body");
  const tooltipEl = el<HTMLDivElement>("tooltip");
  const contextMenuEl = el<HTMLDivElement>("context-menu");
  const searchInput = el<HTMLInputElement>("ref-search");
  const suggestionsList = el<HTMLUListElement>("ref-suggestions");

  let detailsPlz: string | null = null;
  let detailsRequestId = 0;

  // Latest decoded pairwise row (keyed by PLZ), refreshed once per render() regardless of which
  // metric is active — the tooltip and details panel read it synchronously (no re-fetch), and
  // it also drives the reach dimming pass. `null` until the first successful row fetch.
  let latestPairwiseByRef: { refPlz: string; byPlz: Map<string, ReachValue> } | null = null;
  let latestReachBand = 0;

  function findMetric(id: string): RegistryMetric {
    const m = registry.metrics.find((x) => x.id === id);
    if (!m) throw new Error(`unknown metric "${id}" in state`);
    return m;
  }

  function setError(message: string | null): void {
    if (message === null) {
      errorBanner.hidden = true;
      errorBanner.textContent = "";
    } else {
      errorBanner.hidden = false;
      errorBanner.textContent = message;
    }
  }

  function openDetails(plz: string): void {
    detailsPlz = plz;
    detailsEl.hidden = false;
    renderDetailsPanel();
  }

  function closeDetails(): void {
    detailsPlz = null;
    detailsEl.hidden = true;
  }

  /**
   * The details table shows every registry metric, not just the currently-selected one, so it
   * must lazily fetch every metric file (cached after first load) before it can show real
   * numbers. Paint immediately with whatever is already cached — the active metric's file
   * always is, since it's what the map itself just rendered, so the chart is correct straight
   * away — and let renderDetails show a "Loading…" row for anything still in flight. A request
   * token guards against a stale fetch overwriting a newer click/state change.
   */
  async function renderDetailsPanel(): Promise<void> {
    if (!detailsPlz) return;
    const myRequestId = ++detailsRequestId;
    const state = store.get();

    const paint = () => {
      if (!detailsPlz) return;
      renderDetails(
        detailsBodyEl,
        {
          plz: detailsPlz,
          refPlz: state.ref,
          index: plzIndex,
          registry,
          activeMetric: findMetric(state.metric),
          metricCache,
          month: state.month,
          pairwise:
            pairwiseRuntime && latestPairwiseByRef && latestPairwiseByRef.refPlz === state.ref
              ? {
                  metric: pairwiseRuntime.metric,
                  value: latestPairwiseByRef.byPlz.get(detailsPlz),
                  definition: pairwiseRuntime.matrix.meta.definition,
                }
              : null,
        },
        (plz) => store.set({ ref: plz }),
      );
    };

    paint(); // first paint: chart is correct now; table shows "Loading…" for uncached metrics

    // Pairwise metrics have no MetricFile to fetch — their table row comes from the matrix row
    // decoded above, not from metricCache.
    const pending = registry.metrics.filter((m) => m.kind !== "pairwise" && !metricCache.has(m.id));
    if (pending.length === 0) return;
    try {
      await Promise.all(pending.map((m) => loadMetricCached(m)));
    } catch (err) {
      if (myRequestId === detailsRequestId) {
        setError(`Could not load all metrics for the details panel: ${err instanceof Error ? err.message : String(err)}`);
      }
      return;
    }
    if (myRequestId !== detailsRequestId) return; // superseded by a newer click/state change
    paint();
  }

  function positionNearPoint(target: HTMLElement, point: { x: number; y: number }, container: HTMLElement): void {
    const rect = container.getBoundingClientRect();
    const margin = 12;
    let left = rect.left + point.x + margin;
    let top = rect.top + point.y + margin;
    // clamp so the popup never causes horizontal/vertical page scroll, even at 400px width
    const maxLeft = window.innerWidth - target.offsetWidth - 8;
    const maxTop = window.innerHeight - target.offsetHeight - 8;
    left = Math.min(Math.max(8, left), Math.max(8, maxLeft));
    top = Math.min(Math.max(8, top), Math.max(8, maxTop));
    target.style.left = `${left}px`;
    target.style.top = `${top}px`;
  }

  function hideContextMenu(): void {
    contextMenuEl.hidden = true;
    contextMenuEl.innerHTML = "";
  }

  const mapView = new EscapeMapView(el<HTMLDivElement>("map"), plzSource, outline, {
    onHover(plz, point) {
      if (!plz) {
        tooltipEl.hidden = true;
        return;
      }
      const state = store.get();
      const pairwiseForRef = latestPairwiseByRef && latestPairwiseByRef.refPlz === state.ref ? latestPairwiseByRef : null;
      const html = tooltipHtml({
        plz,
        index: plzIndex,
        refPlz: state.ref,
        metric: findMetric(state.metric),
        metricFile: metricCache.get(state.metric),
        month: state.month,
        pairwise: pairwiseForRef ? { value: pairwiseForRef.byPlz.get(plz), bandMinutes: latestReachBand } : undefined,
      });
      if (!html) {
        tooltipEl.hidden = true;
        return;
      }
      tooltipEl.innerHTML = html;
      tooltipEl.hidden = false;
      positionNearPoint(tooltipEl, point, mapView.map.getContainer());
    },
    onClick(plz) {
      openDetails(plz);
    },
    onContextAction(plz, point) {
      const record = plzIndex[plz];
      if (!record) return;
      contextMenuEl.innerHTML = `<button type="button">Set "${plz} ${record.name}" as reference</button>`;
      contextMenuEl.hidden = false;
      positionNearPoint(contextMenuEl, point, mapView.map.getContainer());
      contextMenuEl.querySelector("button")?.addEventListener("click", () => {
        store.set({ ref: plz });
        hideContextMenu();
      });
    },
  });

  document.addEventListener("click", (e) => {
    if (!contextMenuEl.contains(e.target as Node)) hideContextMenu();
  });

  el<HTMLButtonElement>("details-close").addEventListener("click", closeDetails);

  // Bottom sheet collapse (mobile only; the handle is hidden by CSS above 700px).
  const panelEl = el<HTMLElement>("panel");
  const panelHandle = el<HTMLButtonElement>("panel-handle");
  panelHandle.addEventListener("click", () => {
    const collapsed = panelEl.classList.toggle("collapsed");
    panelHandle.setAttribute("aria-expanded", String(!collapsed));
  });

  initSearchBox(plzIndex, {
    input: searchInput,
    list: suggestionsList,
    onSelect: (plz) => store.set({ ref: plz }),
  });

  document.addEventListener("keydown", (e) => {
    const target = e.target as HTMLElement | null;
    const inField = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA";
    if (e.key === "Escape") {
      closeDetails();
      hideContextMenu();
      return;
    }
    if (inField) return;
    const state = store.get();
    const metric = findMetric(state.metric);
    if (!metric.dims.month) return;
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      e.preventDefault();
      const dir = e.key === "ArrowRight" ? 1 : -1;
      const current = state.month === "year" ? (dir === 1 ? 0 : 1) : state.month;
      let next = current + dir;
      if (next < 1) next = 12;
      if (next > 12) next = 1;
      store.set({ month: next as MonthSelection });
    }
  });

  /** Fetch (or reuse the cached) pairwise row for `refPlz` and decode it into `latestPairwiseByRef`.
   * A no-op when there's no pairwise matrix, or the cached row is already for this reference. */
  async function updatePairwiseContext(refPlz: string): Promise<void> {
    if (!pairwiseRuntime) {
      latestPairwiseByRef = null;
      return;
    }
    if (latestPairwiseByRef && latestPairwiseByRef.refPlz === refPlz) return;
    try {
      const row = await pairwiseRuntime.matrix.rowFor(refPlz);
      if (!row) {
        latestPairwiseByRef = null;
        return;
      }
      const decoded = decodeRow(row, pairwiseRuntime.matrix.meta.encoding);
      const byPlz = new Map<string, ReachValue>();
      pairwiseRuntime.matrix.meta.plz_order.forEach((plz, i) => byPlz.set(plz, decoded[i]));
      latestPairwiseByRef = { refPlz, byPlz };
    } catch (err) {
      console.warn(`could not load travel-time row for ${refPlz}: ${err instanceof Error ? err.message : String(err)}`);
      latestPairwiseByRef = null;
    }
  }

  interface ReachSummary {
    reachableCount: number;
    total: number;
  }

  function reachLegendInfo(state: AppState, reachSummary: ReachSummary | null) {
    if (!reachSummary || state.reach <= 0) return undefined;
    return {
      bandMinutes: state.reach,
      reachableCount: reachSummary.reachableCount,
      total: reachSummary.total,
      refPlz: state.ref,
      refRecord: plzIndex[state.ref],
    };
  }

  async function renderWeatherMetric(metric: RegistryMetric, state: AppState, reachSummary: ReachSummary | null): Promise<void> {
    let file: MetricFile;
    try {
      file = await loadMetricCached(metric);
    } catch (err) {
      setError(`Could not load ${metric.file}: ${err instanceof Error ? err.message : String(err)}`);
      return;
    }
    setError(null);

    const dimSpec = metric.dims.month;

    if (!dimSpec) {
      (monthPickerEl.parentElement ?? monthPickerEl).hidden = true;
    } else {
      (monthPickerEl.parentElement ?? monthPickerEl).hidden = false;
      const labels = dimSpec.labels.length === 12 ? dimSpec.labels : MONTH_LABELS_FALLBACK;
      const items: SegmentedItem[] = labels.map((label, i) => ({ value: String(i + 1), label }));
      if (dimSpec.annual !== "none") items.push({ value: "year", label: "Year" });
      renderSegmented(monthPickerEl, items, String(state.month), (v) =>
        store.set({ month: (v === "year" ? "year" : Number(v)) as MonthSelection }),
      );
    }

    (viewToggleEl.parentElement ?? viewToggleEl).hidden = false;
    renderSegmented(
      viewToggleEl,
      [
        { value: "diff", label: "Difference" },
        { value: "abs", label: "Absolute" },
      ],
      state.view,
      (v) => store.set({ view: v as "diff" | "abs" }),
    );

    // --- per-PLZ values (single pass; no per-feature DOM work) ---
    const plzKeys = Object.keys(plzIndex);
    const refValue = valueFor(file.values[state.ref], state.month, dimSpec);
    const rawValues: (number | null)[] = new Array(plzKeys.length);
    const diffs: (number | null)[] = new Array(plzKeys.length);
    const t0 = performance.now();
    plzKeys.forEach((plz, i) => {
      const v = valueFor(file.values[plz], state.month, dimSpec);
      rawValues[i] = v;
      diffs[i] = difference(v, refValue);
    });

    const scaleSpec = state.view === "diff" ? metric.diff : metric.abs;
    if (!scaleSpec) {
      setError(`Metric "${metric.id}" has no ${state.view} colour scale.`);
      return;
    }
    const domain = state.view === "diff" ? diffDomain(scaleSpec, diffs) : absDomain(scaleSpec, rawValues);
    const stops = stopsFor(scaleSpec, domain, palettes.diverging[scaleSpec.palette], palettes.sequential[scaleSpec.palette]);

    mapView.setFillExpression(fillColorExpression(stops));
    const valueMap = new Map<string, number | null>();
    plzKeys.forEach((plz, i) => valueMap.set(plz, state.view === "diff" ? diffs[i] : rawValues[i]));
    mapView.setValues(valueMap);
    mapView.setReference(state.ref, plzIndex);
    const t1 = performance.now();
    console.log(`[escapemap] recolour (${plzKeys.length} PLZs): ${(t1 - t0).toFixed(2)} ms`);

    renderLegend(legendEl, {
      extraAttribution: pairwiseRuntime ? TRAVEL_TIME_ATTRIBUTION : undefined,
      metric,
      view: state.view,
      stops,
      domain,
      refPlz: state.ref,
      refRecord: plzIndex[state.ref],
      reach: reachLegendInfo(state, reachSummary),
    });
  }

  async function renderPairwiseMetric(metric: RegistryMetric, state: AppState, reachSummary: ReachSummary | null): Promise<void> {
    // Difference/Absolute and Month are meaningless for a pairwise metric: no dims, and the
    // reference is 0 by construction (docs/design-isochrones.md §2.7).
    (monthPickerEl.parentElement ?? monthPickerEl).hidden = true;
    (viewToggleEl.parentElement ?? viewToggleEl).hidden = true;

    if (!pairwiseRuntime || pairwiseRuntime.metric.id !== metric.id) {
      setError(`"${metric.label}" is unavailable (travel-time data not published yet).`);
      return;
    }
    if (!latestPairwiseByRef || latestPairwiseByRef.refPlz !== state.ref) {
      setError(`Could not load travel times for reference ${state.ref}.`);
      return;
    }
    setError(null);

    const plzKeys = Object.keys(plzIndex);
    const valueMap = new Map<string, number | null>();
    const t0 = performance.now();
    plzKeys.forEach((plz) => {
      const v = latestPairwiseByRef!.byPlz.get(plz);
      // "beyond the cap" paints as the darkest colour (interpolate clamps past its last stop);
      // the tooltip/details panel show the real "> 8 h" text separately.
      valueMap.set(plz, v === "beyond" ? Number.POSITIVE_INFINITY : (v ?? null));
    });

    const scaleSpec = metric.abs;
    const domain: [number, number] = scaleSpec.domain ?? [0, 240];
    const stops = stopsFor(scaleSpec, domain, undefined, palettes.sequential[scaleSpec.palette]);

    mapView.setFillExpression(fillColorExpression(stops));
    mapView.setValues(valueMap);
    mapView.setReference(state.ref, plzIndex);
    const t1 = performance.now();
    console.log(`[escapemap] pairwise recolour (${plzKeys.length} PLZs): ${(t1 - t0).toFixed(2)} ms`);

    renderLegend(legendEl, {
      extraAttribution: pairwiseRuntime ? TRAVEL_TIME_ATTRIBUTION : undefined,
      metric,
      view: "abs",
      stops,
      domain,
      refPlz: state.ref,
      refRecord: plzIndex[state.ref],
      reach: reachLegendInfo(state, reachSummary),
    });
  }

  async function render(): Promise<void> {
    const state: AppState = store.get();
    let metric: RegistryMetric;
    try {
      metric = findMetric(state.metric);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      return;
    }

    // --- metric picker (shared by both branches) ---
    renderSegmented(
      metricPickerEl,
      registry.metrics.map((m): SegmentedItem => ({ value: m.id, label: m.short, title: m.label })),
      metric.id,
      (id) => store.set({ metric: id }),
    );

    // --- pairwise row for the current reference: drives both the reach filter (every metric)
    // and, when active, the travel-time metric's own colouring. ---
    await updatePairwiseContext(state.ref);
    latestReachBand = state.reach;

    if (pairwiseRuntime) {
      reachGroupEl.hidden = false;
      const range = pairwiseRuntime.metric.band_range;
      if (range) {
        // Built once; re-rendering during a drag would drop the thumb.
        reachSlider ??= createReachSlider(reachPickerEl, reachValueEl, {
          ...range,
          bands: pairwiseRuntime.metric.bands ?? [],
          onChange: (minutes) => store.set({ reach: minutes }),
        });
        reachSlider.update(state.reach);
      } else {
        // Registry without a band_range: one button per band.
        reachValueEl.textContent = "";
        const bands = pairwiseRuntime.metric.bands ?? [];
        const items: SegmentedItem[] = [
          { value: "0", label: "Any" },
          ...bands.map((b): SegmentedItem => ({ value: String(b), label: formatBandLabel(b) })),
        ];
        renderSegmented(reachPickerEl, items, String(state.reach), (v) => store.set({ reach: Number(v) }));
      }
    } else {
      reachGroupEl.hidden = true;
    }

    // --- reach dimming feature-state: one synchronous loop over every PLZ, applies regardless
    // of which metric is on screen (docs/design-isochrones.md §3.3). ---
    let reachSummary: ReachSummary | null = null;
    if (latestPairwiseByRef) {
      const reachMap = new Map<string, 0 | 1>();
      let count = 0;
      for (const [plz, v] of latestPairwiseByRef.byPlz) {
        const ok = state.reach <= 0 || (typeof v === "number" && v <= state.reach);
        reachMap.set(plz, ok ? 1 : 0);
        if (ok) count++;
      }
      mapView.setReach(reachMap);
      reachSummary = { reachableCount: count, total: latestPairwiseByRef.byPlz.size };
    }

    if (metric.kind === "pairwise") {
      await renderPairwiseMetric(metric, state, reachSummary);
    } else {
      await renderWeatherMetric(metric, state, reachSummary);
    }

    // A standing data-integrity problem (meta/plz_index mismatch) always wins the banner, even
    // over a successful render of an unrelated metric.
    if (pairwiseError) setError(pairwiseError);

    renderDetailsPanel();
  }

  store.subscribe(() => {
    render().catch((err) => setError(err instanceof Error ? err.message : String(err)));
  });

  await mapView.whenReady();
  await render();
  loading.hidden = true;
}

main().catch((err) => {
  const loading = document.getElementById("loading");
  if (loading) loading.hidden = true;
  const banner = document.getElementById("error-banner");
  const message = `Failed to load EscapeMap: ${err instanceof Error ? err.message : String(err)}`;
  if (banner) {
    banner.hidden = false;
    banner.textContent = message;
  }
  console.error(err);
});
